import find from 'lodash/find'
import forEach from 'lodash/forEach'
import map from 'lodash/map'
import reduce from 'lodash/reduce'
import sortBy from 'lodash/sortBy'
import split from 'lodash/split'
import trim from 'lodash/trim'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'
import { createLoader, parseAsArrayOf, parseAsInteger, parseAsString, parseAsStringEnum } from 'nuqs'

import { ProductListBlockFacetType, ProductListBlockFragmentFragment } from '@Generated/graphql'
import { FacetDynamicConfig, FacetParameter, FacetStats, NumericFilter, Options, ProductSorting } from '@Globals/Algolia/Types'
import type { SplitTests } from '@Globals/SplitTests/types'

export type FacetConfig = ProductListBlockFragmentFragment['facetConfig']
type FacetConfigItem = NonNullable<FacetConfig>[number]

// The values of useQueryStates(createQueryStatesKeys(facetConfig)) on the
// client and of loadQueryStates on the server: page (number), sorting
// (ProductSorting), q (string | null) and one entry per facet parameter
// (string[] for enumerable facets, number[] for ranges, null when absent).
export type QueryStates = Record<string, unknown>

const ENUMERABLE_TYPES = [
  ProductListBlockFacetType.EnumerableDefault,
  ProductListBlockFacetType.EnumerableBoolean,
  ProductListBlockFacetType.EnumerableColor,
  ProductListBlockFacetType.EnumerableNumeric,
]
const RANGE_TYPES = [ProductListBlockFacetType.RangeDefault, ProductListBlockFacetType.RangePrice]

export function parseHitsPerPage(limit: number | null | undefined, usePagination: boolean): number {
  if (limit && Number.isInteger(limit) && limit > 0) {
    return limit
  }

  return usePagination ? 60 : 1000
}

// The values selected for a facet, deduplicated: the URL form is one
// comma-separated list per facet parameter.
const selectedValues = (values: QueryStates, facet: FacetConfigItem): unknown[] | null => {
  const selected = facet.parameter ? values[facet.parameter] : null

  return Array.isArray(selected) ? uniq(selected) : null
}

const parseValidFacetFilters = (values: QueryStates, config: FacetConfig): string[] => sortBy(
  reduce(
    config,
    (memo, facet) => {
      const selected = selectedValues(values, facet)

      if (!facet.id || !facet.type || !ENUMERABLE_TYPES.includes(facet.type) || !selected) {
        return memo
      }

      // Only add values that exist for the given facet
      return reduce(
        selected,
        (memoInner, valueKey) => {
          if (typeof valueKey !== 'string') {
            return memoInner
          }

          // "__" is only group syntax when the facet has groups; free-text
          // values (looks ambassador handles) may contain it literally.
          if (valueKey.includes('__') && facet.groups && facet.groups.length !== 0) {
            const [groupKey] = split(valueKey, '__')
            const facetGroup = find(facet.groups, ['id', groupKey])
            if (!facetGroup) {
              return memoInner
            }

            const facetValue = find(facetGroup.values, ['id', valueKey])
            if (!facetValue) {
              return memoInner
            }

            return [...memoInner, `${facet.id}:${facetValue.id}`]
          }

          const facetValue = find(facet.values, ['id', valueKey])
          if (!facetValue) {
            return memoInner
          }

          return [...memoInner, `${facet.id}:${facetValue.id}`]
        },
        memo,
      )
    },
    [] as string[],
  ),
)

const parseValidNumericFilters = (
  values: QueryStates,
  config: FacetConfig,
  facetStats: FacetStats,
) => reduce(
  config,
  (memo, facet) => {
    const selected = selectedValues(values, facet)
    const stats = facetStats[facet.id || '']

    if (!facet.id || !facet.type || !RANGE_TYPES.includes(facet.type) || !selected || !stats) {
      return memo
    }

    // A single value is a point range
    const bounds = selected.length === 1 ? [selected[0], selected[0]] : selected

    if (bounds.length !== 2 || typeof bounds[0] !== 'number' || typeof bounds[1] !== 'number') {
      return memo
    }

    const min = Math.max(bounds[0], 0)
    const max = Math.min(bounds[1], stats.max)

    if (min > max || (min < stats.min && max > stats.max)) {
      return memo
    }

    return {
      ...memo,
      [facet.id]: {
        max,
        min,
      },
    }
  },
  {} as Record<string, NumericFilter>,
)

export const parseValidPage = (page: unknown): number => (
  typeof page === 'number' && Number.isInteger(page) && page >= 1 && page <= 1000 ? page : 1
)

const parseValidSorting = (sorting: unknown): ProductSorting => (
  Object.values(ProductSorting).includes(sorting as ProductSorting) ? sorting as ProductSorting : ProductSorting.Default
)

// The search text; '' when absent so the client and SSR options serialize
// identically.
export const parseValidQuery = (q: unknown): string => trim(typeof q === 'string' ? q : '')

// Validates the query states against the block's facet config: unknown
// facets and values are dropped, ranges are clamped to the facet stats, the
// page to 1-1000. Both the client contexts and the SSR blocks derive their
// Algolia options (the SWR key) from this.
export const parseValidQueryStates = (
  values: QueryStates,
  config: FacetConfig,
  facetStats: FacetStats,
) => ({
  facetFilters: parseValidFacetFilters(values, config),
  numericFilters: parseValidNumericFilters(values, config, facetStats),
  sorting: parseValidSorting(values.sorting),
  page: parseValidPage(values.page),
})

// countAppliedFilters counts active filter_* query states. Shared by the
// products and looks search providers.
export function countAppliedFilters(queryStates: Record<string, unknown>): number {
  return reduce(
    queryStates,
    (memo, item, key) => {
      if (!item || !key.startsWith('filter_')) {
        return memo
      }

      if (Array.isArray(item)) {
        return memo + item.length
      }

      return memo + 1
    },
    0,
  )
}

// computeFacetDynamicConfig derives the per-facet checked/count/disabled state
// from the current filters and the Algolia facet response. Shared by the
// products and looks search providers.
export function computeFacetDynamicConfig(
  facetConfig: ProductListBlockFragmentFragment['facetConfig'],
  facetFilters: string[],
  numericFilters: Record<string, NumericFilter>,
  facets: Record<string, Record<string, number>> | undefined,
  facetsStats: FacetStats | undefined,
): Record<string, FacetDynamicConfig> {
  return reduce(facetConfig, (memo, facet) => { /* eslint-disable no-param-reassign */
    if (facet.type === ProductListBlockFacetType.EnumerableDefault
      || facet.type === ProductListBlockFacetType.EnumerableBoolean
      || facet.type === ProductListBlockFacetType.EnumerableColor
      || facet.type === ProductListBlockFacetType.EnumerableNumeric
    ) {
      let checkedCount = 0
      let groupCount = 0

      forEach(facet.values, (value) => {
        const checked = facetFilters.includes(`${facet.id}:${value.id}`)
        const count = facets?.[facet.id!]?.[value.id!] || 0

        if (checked) {
          checkedCount += 1
        }

        memo[`${facet.parameter}:${value.id}`] = {
          checked,
          count,
          disabled: count === 0,
          key: value.key || '',
          name: value.name || null,
          type: 'value',
        }
      })

      forEach(facet.groups, (group) => {
        forEach(group.values, (value) => {
          const checked = facetFilters.includes(`${facet.id}:${value.id}`)
          const count = facets?.[facet.id!]?.[value.id!] || 0

          if (checked) {
            checkedCount += 1
          }

          memo[`${facet.parameter}:${value.id}`] = {
            checked,
            count,
            disabled: count === 0,
            key: value.key || '',
            name: value.name || null,
            type: 'value',
          }
        })

        groupCount += 1
      })

      memo[facet.parameter!] = {
        count: checkedCount,
        disabled: false,
        name: facet.name || null,
        showGroupTitle: groupCount > 1 && facet.key !== 'colorShades',
        type: 'facet',
      }
    }

    if (facet.type === ProductListBlockFacetType.RangeDefault || facet.type === ProductListBlockFacetType.RangePrice) {
      const numericFilter = numericFilters?.[facet.id!]
      memo[facet.parameter!] = {
        count: numericFilter ? 1 : 0,
        disabled: !!facetsStats?.[facet.id!],
        name: facet.name || null,
        showGroupTitle: false,
        type: 'facet',
      }
    }

    return memo
  }, {} as Record<string, FacetDynamicConfig>)
}

export { processURLSearchParams } from '@Globals/SearchParams'
export const queryParameterEnumerable = parseAsArrayOf(parseAsString)
export const queryParameterRange = parseAsArrayOf(parseAsInteger)
export const queryParameterPage = parseAsInteger.withDefault(1)
export const queryParameterSorting = parseAsStringEnum<ProductSorting>(Object.values(ProductSorting)).withDefault(ProductSorting.Default)

export function createQueryStatesKeys(facetConfig: FacetParameter[] | null | undefined) {
  return Object.fromEntries(
    sortBy(
      uniqBy(
        [
          ['page', queryParameterPage],
          ['sorting', queryParameterSorting],
          ['q', parseAsString],
          ...map(facetConfig, (facet) => {
            if (facet.type === ProductListBlockFacetType.RangeDefault || facet.type === ProductListBlockFacetType.RangePrice) {
              return [facet.parameter, queryParameterRange]
            }
            return [facet.parameter, queryParameterEnumerable]
          }),
        ],
        '0',
      ),
      0,
    ),
  )
}

// Server-side twin of useQueryStates(createQueryStatesKeys(facetConfig)):
// the request query parsed by the same parsers, so the SSR options (the SWR
// fallback key) and the client options come from one parse.
export const loadQueryStates = (
  query: Record<string, string | string[] | undefined>,
  facetConfig: FacetParameter[] | null | undefined,
): QueryStates => createLoader(createQueryStatesKeys(facetConfig))(query) as QueryStates

/** The block configuration a product list's query derives from; the CMS block and the platform block both supply it. */
export interface BlockQueryConfig {
  facets: string[]
  filters: string
  limit: number | null | undefined
  optionalFilters: string[]
  /** `groups` vetoes the grouped experience (on unless set); `search` enables `?q=` (off unless set). */
  settings: { groups?: unknown, search?: unknown }
}

export interface BlockQueryRequest {
  locale: string
  queryStates: QueryStates
  splitTests: SplitTests
  /** Whether the block owns the page's query state: pagination, `?q=` and the request's page apply only then. */
  usePagination: boolean
}

/**
 * The base options of a product list block's query, as the pages router's SSR (generateOptions) and the platform's
 * products resolver build them. Every key is always set, never conditionally omitted: the client's options carry
 * the same keys and the serialised SWR keys must match for fallback hydration, so this must mirror the client's
 * ProductSearchProvider computation exactly.
 */
export const blockQueryOptions = (block: BlockQueryConfig, request: BlockQueryRequest): Options => {
  // b and c share the thumbnails experience and groupProducts retrieval; they differ only in Algolia distinct
  // (b collapses per colour group). The block's groups setting (graph-api folds the block's product count into
  // it) vetoes the whole grouping experience: no distinct collapse and no groupProducts retrieval, so the b/c
  // tiles show only the product itself in the variant selector.
  const groupedProductsGroup = request.splitTests.grouped_products_v2?.group
  const { settings } = block
  const enableGrouping = 'groups' in settings ? Boolean(settings.groups) : true
  const enableSearch = 'search' in settings ? Boolean(settings.search) : false

  return {
    distinct: groupedProductsGroup === 'b' && enableGrouping,
    facetFilters: [],
    facets: block.facets,
    filters: block.filters,
    grouped: (groupedProductsGroup === 'b' || groupedProductsGroup === 'c') && enableGrouping,
    hitsPerPage: parseHitsPerPage(block.limit, request.usePagination).toString(),
    locale: request.locale,
    numericFilters: {},
    optionalFilters: block.optionalFilters,
    page: request.usePagination ? parseValidPage(request.queryStates.page) : null,
    pagination: request.usePagination,
    query: enableSearch ? parseValidQuery(request.queryStates.q) : undefined,
    sorting: ProductSorting.Default,
    sortingGroup: request.splitTests.sorting_v1?.group || 'a',
  }
}

/**
 * The query state's filters and sorting, when any applies, for the second query the SSR runs on a filtered
 * request: validated against the facet config, the ranges clamped against the unfiltered result's stats. Null
 * when the base options already are the request's.
 */
export const appliedQueryStates = (
  queryStates: QueryStates,
  facetConfig: FacetConfig,
  facetStats: FacetStats,
): Pick<Options, 'facetFilters' | 'numericFilters' | 'sorting'> | null => {
  const parsed = parseValidQueryStates(queryStates, facetConfig, facetStats)
  const applies = parsed.facetFilters.length !== 0 || Object.keys(parsed.numericFilters).length !== 0 || parsed.sorting !== ProductSorting.Default
  return applies ? { facetFilters: parsed.facetFilters, numericFilters: parsed.numericFilters, sorting: parsed.sorting } : null
}
